import React, { Children, memo, useCallback, useLayoutEffect, useRef, useState } from "react";

interface IBookingParticipantNameProps {
  firstName: string;
  lastName: string;
  divisionWidth: number | null;
  children?: React.ReactNode;
}

function getTrailingSiblingsWidth(container: HTMLElement, nameRow: HTMLElement): number {
  let width = 0;

  for (const child of Array.from(container.children)) {
    if (child === nameRow) {
      continue;
    }

    const element = child as HTMLElement;
    const style = getComputedStyle(element);
    width +=
      element.getBoundingClientRect().width +
      parseFloat(style.marginLeft) +
      parseFloat(style.marginRight);
  }

  return width;
}

function BookingParticipantNameComponent({
  firstName,
  lastName,
  divisionWidth,
  children,
}: IBookingParticipantNameProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const nameRowRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [useInitial, setUseInitial] = useState(false);
  const trailingChildCount = Children.count(children);

  const fullName = lastName ? `${firstName} ${lastName}` : firstName;

  const updateUseInitial = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    const nameRow = nameRowRef.current;

    if (!container || !measure || !nameRow) {
      return;
    }

    const siblingsWidth = getTrailingSiblingsWidth(container, nameRow);
    const availableWidth = container.clientWidth - siblingsWidth;
    setUseInitial(measure.scrollWidth > availableWidth);
  }, [fullName]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    updateUseInitial();

    const observer = new ResizeObserver(updateUseInitial);
    observer.observe(container);

    return () => observer.disconnect();
  }, [firstName, lastName, divisionWidth, trailingChildCount, updateUseInitial]);

  useLayoutEffect(() => {
    if (useInitial) {
      return;
    }

    const text = textRef.current;

    if (text && text.scrollWidth > text.clientWidth) {
      setUseInitial(true);
    }
  }, [useInitial, firstName, lastName, divisionWidth, trailingChildCount]);

  const displayName = useInitial
    ? lastName
      ? `${firstName.charAt(0)}. ${lastName}`
      : firstName
    : fullName;

  return (
    <span ref={containerRef} className="booking-participant-name booking-participant-name--tablet">
      <span ref={nameRowRef} className="booking-participant-name-row">
        <span ref={measureRef} className="booking-participant-name-measure" aria-hidden="true">
          {fullName}
        </span>
        <span ref={textRef} className="booking-participant-name-text">
          {displayName}
        </span>
      </span>
      {children}
    </span>
  );
}

const BookingParticipantName = memo(BookingParticipantNameComponent);

export default BookingParticipantName;
